import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import AppLayout from '@layouts/AppLayout';

import { StateContext } from '@store/store';
import HeaderWithCTA from '@components/header-with-cta';
import { fetchBlogsByAuthor, fetchAuthorDetails } from '@lib/API/authorAPI';
import AuthorHeaderSection from '@components/author-header-section';
import { AuthorDetailsType, BlogCardProps } from 'src/types';
import ivclass from '@invideoio/web-shared/Modules/IVClass';
import BackButton from '@components/back-button';
import BlogSearchCard from '@components/blog-posts/blog-search-card';
import ShowMore from '@components/showmore-button';
import { Pagination } from '@constants/common';
import useBlogAppLoad from '@hooks/useBlogAppLoad';

interface ServerSidePropsType {
  query: {
    author: string;
  };
}

export async function getServerSideProps(context: ServerSidePropsType) {
  const { author } = context.query;
  const authorDetails = await fetchAuthorDetails(author);
  return {
    props: { authorDetails },
  };
}

interface AuthorsPageProps {
  authorDetails: AuthorDetailsType;
}

const AuthorsPage = ({ authorDetails }: AuthorsPageProps) => {
  const globalState = useContext(StateContext);

  const { platform, browser } = globalState;

  const router = useRouter();

  const [blogList, setBlogList] = useState<BlogCardProps[]>([]);
  const [totalBlogCount, setTotalBlogCount] = useState<number>(0);

  useBlogAppLoad({ platform });

  async function loadMoreBlogs() {
    const { list, totalBlogCount } = await fetchBlogsByAuthor({
      authorSlug: router.query.author?.toString(),
      limit: Pagination.AUTHORS_BLOG_PER_PAGE,
      start: blogList.length,
    });
    setBlogList(blogList => [...blogList, ...list]);
    setTotalBlogCount(totalBlogCount);
  }

  useEffect(() => {
    loadMoreBlogs();
  }, []);

  return (
    <AppLayout platform={platform}>
      <HeaderWithCTA />
      <AuthorHeaderSection
        platform={platform}
        browser={browser}
        pathName={router.asPath}
        {...authorDetails}
      />
      <section
        className={ivclass(
          'iv-pt-[72px] md:iv-pt-[57px] sm:iv-pt-50',
          'iv-pb-[100px]',
          'iv-bg-black-4',
        )}
      >
        <div className={ivclass('iv-container iv-mx-auto')}>
          <div className={ivclass('iv-grid iv-grid-cols-12')}>
            <div
              className={ivclass(
                'iv-col-span-10 lg:iv-col-span-11 md:iv-col-span-12 sm:iv-col-span-12 ',
                'iv-col-start-2 lg:iv-col-start-2 md:iv-col-start-1 sm:iv-col-start-1',
                'iv-px-20 lg:iv-px-[0] md:iv-px-25',
              )}
            >
              <div
                className={ivclass(
                  'lg:iv-px-35',
                  'sm:iv-flex sm:iv-justify-center',
                )}
              >
                <BackButton
                  isDark={true}
                  buttonContent="Back to Blog"
                  redirectLink="/blog/"
                />
              </div>
              <div
                className={ivclass(
                  'iv-grid iv-grid-cols-12',
                  'iv-gap-x-40 iv-gap-y-100 ',
                  'md:iv-gap-x-[0] md:iv-gap-y-[73px] ',
                  'sm:iv-gap-x-[0] sm:iv-gap-y-[73px] ',
                  'iv-mt-100 md:iv-mt-[75px] sm:iv-mt-50',
                  'lg:iv-pl-[35px] lg:iv-pr-50',
                )}
              >
                {blogList.length > 0 &&
                  blogList.map(post => (
                    <div
                      key={post.id}
                      className={ivclass(
                        'iv-col-span-6 md:iv-col-span-12 sm:iv-col-span-12',
                      )}
                    >
                      <BlogSearchCard
                        title={post.title}
                        slug={post.slug}
                        image={post.featureImage}
                      />
                    </div>
                  ))}
              </div>
              <div className={ivclass('iv-mt-100 md:iv-mt-80 sm:iv-mt-50')}>
                <ShowMore
                  isPaginationEnded={blogList.length == totalBlogCount}
                  showMoreAction={loadMoreBlogs}
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </AppLayout>
  );
};

export default AuthorsPage;
